import { useMemo, useEffect, useState, useRef } from 'react';
import { __, _n, sprintf } from '@wordpress/i18n';
import { PitchFieldContext } from './context.js';
import { useFieldData } from '@/hooks/useFieldData.js';
import { useOccupiedTiles } from '@/hooks/useOccupiedTiles.js';
import { useOccupiedStore } from '@/hooks/useOccupied.js';
import { useSelectionStore, useSelectionActions } from '@/hooks/useSelection.js';
import { useInputState } from '@/hooks/useInputState.js';
import { usePriceInputSync } from '@/hooks/usePriceInputSync.js';
import { buildGrid } from '@/utils/grid.js';
import { buildColorVars } from '@/utils/colorVars.js';

import Grid from './Grid.jsx';
import PriceDisplay from './PriceDisplay.jsx';
import Legend from './Legend.jsx';

/**
 * PitchField — compound component root.
 *
 * Provides a *stable* context that contains only static/config data.
 * Dynamic state lives in dedicated Zustand stores:
 *  - useSelectionStore  — selected tile IDs
 *  - useOccupiedStore   — occupied/pending tiles (polled every 30 s)
 *  - useKeyboardFocusStore — keyboard focus state
 *
 * This means background API polls never cascade into a full tree re-render.
 * Only the specific tiles whose status changed re-render.
 */
export default function PitchField( { fieldType, mode = 'interactive', className, children } ) {
	const { data: fieldConfig, isLoading: loadingField, isError: errorField } = useFieldData( fieldType );

	// Get post ID from input element (backend context) to exclude from occupied tiles
	const excludePostId = useRef( null );
	useEffect( () => {
		const inputEl = document.querySelector( `.wsp-tiles-input[data-field-type="${ fieldType }"]` );
		if ( inputEl?.dataset.postId ) {
			excludePostId.current = parseInt( inputEl.dataset.postId );
		}
	}, [ fieldType ] );

	const { occupied, isLoading: loadingTiles } = useOccupiedTiles( fieldType, mode, excludePostId.current );

	// Conflict notification: number of tile IDs that were auto-deselected
	const [ conflictNotice, setConflictNotice ] = useState( 0 );

	// Auto-dismiss conflict notice after 8 s
	useEffect( () => {
		if ( ! conflictNotice ) return;
		const t = setTimeout( () => setConflictNotice( 0 ), 8000 );
		return () => clearTimeout( t );
	}, [ conflictNotice ] );

	// Build grid once — Grid.jsx reads it from context instead of calling buildGrid() again.
	const grid = useMemo( () => {
		if ( ! fieldConfig ) return null;
		return buildGrid( fieldConfig );
	}, [ fieldConfig ] );

	const tiles             = grid?.tiles             ?? [];
	const normalizedBundles = grid?.normalizedBundles ?? [];

	// Sync state with HTML input elements
	useInputState( fieldType, normalizedBundles, loadingField );
	usePriceInputSync( fieldType, fieldConfig, tiles );

	// Map tileId → bundleId — needed to resolve "#bundleId" occupied keys back to
	// individually-selected tile IDs when auto-deselecting occupied selections.
	const tileToBundleId = useMemo( () => {
		const map = {};
		for ( const tile of tiles ) {
			if ( tile.bundleId ) map[ tile.id ] = tile.bundleId;
		}
		return map;
	}, [ tiles ] );

	// Sync polled occupied data into the store.
	// Store keeps Variant-A compressed format; expandedCache is built internally.
	// After updating the store, check whether any currently-selected tiles for this
	// field are now occupied — if so, auto-deselect them and show a notice.
	useEffect( () => {
		if ( !grid ) return;
		useOccupiedStore.getState().setOccupied( fieldType, occupied, normalizedBundles );

		if ( mode !== 'interactive' ) return;
		const sel         = useSelectionStore.getState().selected[ fieldType ] ?? {};
		const selectedIds = Object.keys( sel );
		if ( ! selectedIds.length ) return;

		// Check which selected tiles are now occupied
		const store = useOccupiedStore.getState();
		const nowOccupied  = selectedIds.filter( ( id ) => !! store.getOccupiedTile( fieldType, id ) );
		if ( ! nowOccupied.length ) return;

		useSelectionStore.getState().deselectMany( fieldType, nowOccupied );
		setConflictNotice( nowOccupied.length );
	}, [ occupied, mode, fieldType, tileToBundleId ] );

	const { toggleTile, clearSelection } = useSelectionActions( fieldType, tiles );

	const colorStyle = useMemo(
		() => buildColorVars( fieldConfig?.colors ?? {} ),
		[ fieldConfig ]
	);

	// Context contains only stable, config-level data — nothing that changes
	// at runtime during user interaction or background polling.
	const ctx = useMemo( () => ( {
		fieldType,
		mode,
		fieldConfig:      fieldConfig ?? null,
		grid,
		tiles,
		normalizedBundles,
		toggleTile,
		clearSelection,
		isLoading: loadingField || loadingTiles,
	} ), [
		fieldType, mode, fieldConfig, grid, tiles, normalizedBundles,
		toggleTile, clearSelection, loadingField, loadingTiles,
	] );

	if ( loadingField ) {
		return (
			<div className={`wsp-pitch-field ${ className ?? '' }`} data-mode={mode}>
				<div
					className="wsp-pitch-field__skeleton"
					role="status"
					aria-label={__( 'Loading…', 'web-sponsors-pitch' )}
				/>
			</div>
		);
	}

	if ( errorField ) {
		return (
			<div className={`wsp-pitch-field ${ className ?? '' }`} data-mode={mode}>
				<p className="wsp-pitch-field__error" role="alert">
					{__( 'The field could not be loaded.', 'web-sponsors-pitch' )}
				</p>
			</div>
		);
	}

	return (
		<PitchFieldContext.Provider value={ctx}>
			<div className={`wsp-pitch-field ${ className ?? '' }`} data-mode={mode} style={colorStyle}>

				{conflictNotice > 0 && (
					<div className="wsp-pitch-field__conflict-notice" role="alert">
						<span>
							{sprintf(
								_n(
									'%d tile is no longer available and was deselected.',
									'%d tiles are no longer available and were deselected.',
									conflictNotice,
									'web-sponsors-pitch'
								),
								conflictNotice
							)}
						</span>
						<button
							type="button"
							className="wsp-pitch-field__conflict-close"
							onClick={() => setConflictNotice( 0 )}
							aria-label={__( 'Dismiss', 'web-sponsors-pitch' )}
						>
							×
						</button>
					</div>
				)}

				{children ?? (
					<>
						<Grid />
						<PriceDisplay />
						<Legend />
					</>
				)}
			</div>
		</PitchFieldContext.Provider>
	);
}

PitchField.Grid         = Grid;
PitchField.PriceDisplay = PriceDisplay;
PitchField.Legend       = Legend;
